import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cpu, Waves, Zap, ChevronRight, Terminal, Check } from 'lucide-react';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const glowingText = "drop-shadow-[0_0_8px_rgba(0,255,65,0.4)]";

export default function App() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    setTimeout(() => {
      setStatus('success');
      setEmail('');
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-gray-300 font-sans selection:bg-[#00FF41] selection:text-black overflow-x-hidden relative">
      
      {/* Background Grid & Noise - Subtle Texture */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#050505]/50 to-[#050505]" />
      </div>

      <main className="relative z-10 flex flex-col items-center w-full max-w-5xl mx-auto px-6">
        
        {/* --- SECTION 1: HERO --- */}
        <section className="h-screen flex flex-col justify-center items-center text-center w-full">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            className="mb-12 relative"
          >
             {/* Abstract Cursor/Shape */}
            <div className="w-16 h-1 bg-[#00FF41] animate-pulse shadow-[0_0_20px_#00FF41]" />
          </motion.div>

          <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-6">
            <motion.h1 
              variants={fadeInUp}
              className={`text-5xl md:text-7xl font-bold text-white tracking-tight ${glowingText}`}
            >
              Where Intelligence Executes.
            </motion.h1>
            
            <motion.h2 
              variants={fadeInUp}
              className="text-2xl md:text-3xl text-gray-400 font-light"
            >
              智能,在此运行。
            </motion.h2>

            <motion.div 
              variants={fadeInUp}
              className="pt-8 font-mono text-sm text-[#00FF41] flex items-center justify-center gap-2"
            >
              <Terminal size={14} />
              <span className="animate-pulse">System Status: Initializing...</span>
            </motion.div>
          </motion.div>
          
          {/* Scroll Indicator */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2, duration: 1 }}
            className="absolute bottom-12 left-1/2 -translate-x-1/2 text-xs font-mono text-gray-600"
          >
            SCROLL TO DECRYPT
          </motion.div>
        </section>


        {/* --- SECTION 2: THE MANIFESTO --- */}
        <section className="min-h-[80vh] flex items-center justify-center w-full py-24">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="w-full max-w-3xl border-l-2 border-[#00FF41]/20 pl-8 md:pl-12 py-4 relative"
          >
            {/* Decorative Code Elements */}
            <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-[#050505] border-2 border-[#00FF41]" />
            <div className="absolute -left-[9px] bottom-0 w-4 h-4 rounded-full bg-[#050505] border-2 border-[#00FF41]/50" />

            <div className="space-y-12 font-mono text-lg md:text-xl leading-relaxed">
              <motion.div variants={fadeInUp}>
                <p className="text-white mb-2">Beyond Wrappers.</p>
                <p className="text-gray-500 text-sm md:text-base">拒绝套壳。</p>
              </motion.div>

              <motion.div variants={fadeInUp}>
                <p className="text-white mb-2">The era of GUI + Chatbot is over. We are witnessing a fundamental shift in software architecture.</p>
                <p className="text-gray-500 text-sm md:text-base">图形界面+聊天机器人的时代已经结束。我们正在见证软件架构的根本性范式转移。</p>
              </motion.div>

              <motion.div variants={fadeInUp}>
                <p className="text-[#00FF41] mb-2 font-bold">Intent is the new Code.</p>
                <p className="text-gray-500 text-sm md:text-base">意图即代码。</p>
              </motion.div>

              <motion.div variants={fadeInUp} className="pt-4 border-t border-gray-800">
                <p className="text-white mb-2">AINATIVE.RUN is strictly curated. No noise. Just the signals of the future.</p>
                <p className="text-gray-500 text-sm md:text-base">AINATIVE.RUN 仅做严选。没有噪音。只有通往未来的信号。</p>
              </motion.div>
            </div>
          </motion.div>
        </section>


        {/* --- SECTION 3: THE CRITERIA --- */}
        <section className="w-full py-24">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h3 className="font-mono text-[#00FF41] text-sm tracking-widest uppercase mb-2">Protocol 01</h3>
            <h2 className="text-3xl text-white font-bold">Selection Protocol <span className="text-gray-600 font-light mx-2">/</span> 甄选协议</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card 
              icon={<Cpu size={32} />}
              title="Model-Centric"
              subTitle="模型驱动"
              desc="The model is the engine, not the passenger."
              delay={0}
            />
            <Card 
              icon={<Waves size={32} />}
              title="Fluid Interface"
              subTitle="流体交互"
              desc="Adapts to user intent dynamically."
              delay={0.2}
            />
            <Card 
              icon={<Zap size={32} />}
              title="Autonomous"
              subTitle="自主执行"
              desc="Capable of reasoning and action."
              delay={0.4}
            />
          </div>
        </section>


        {/* --- SECTION 4: CTA FOOTER --- */}
        <section className="w-full py-32 flex flex-col items-center justify-center text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="w-full max-w-lg p-1"
          >
            <h2 className="text-4xl text-white font-bold mb-2">Join the Sequence.</h2>
            <p className="text-gray-500 mb-8 font-light">加入序列。</p>
            <p className="text-[#00FF41] font-mono text-sm mb-8">
              {'>'} Be the first to know when we launch the Alpha Batch.
            </p>

            <form onSubmit={handleSubscribe} className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-[#00FF41] to-emerald-600 rounded-lg blur opacity-20 group-hover:opacity-50 transition duration-1000"></div>
              <div className="relative flex flex-col sm:flex-row gap-2 bg-[#050505] p-2 rounded-lg border border-gray-800">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="enter_email_address"
                  disabled={status === 'success' || status === 'loading'}
                  className="flex-1 bg-transparent text-white px-4 py-3 outline-none font-mono text-sm placeholder:text-gray-700 disabled:opacity-50"
                  required
                />
                <button
                  type="submit"
                  disabled={status === 'success' || status === 'loading'}
                  className="bg-white text-black hover:bg-[#00FF41] hover:text-black transition-colors px-6 py-3 rounded font-bold text-sm uppercase flex items-center justify-center gap-2 disabled:bg-gray-800 disabled:text-gray-500"
                >
                  {status === 'loading' ? (
                    <span className="animate-pulse">...</span>
                  ) : status === 'success' ? (
                    <>Access Granted <Check size={16} /></>
                  ) : (
                    <>Request Access <ChevronRight size={16} /></>
                  )}
                </button>
              </div>
            </form>
            
            {status === 'success' && (
               <motion.p 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-[#00FF41] font-mono text-xs mt-4"
               >
                 Signal received. We will contact you soon.
               </motion.p>
            )}

          </motion.div>

          <footer className="mt-32 w-full flex flex-col md:flex-row justify-between items-center text-xs font-mono text-gray-700 border-t border-gray-900 pt-8">
            <p>© 2025 AINATIVE.RUN | System Ready.</p>
            <a href="mailto:hello@ainative.run" className="hover:text-[#00FF41] transition-colors mt-2 md:mt-0">
              hello@ainative.run
            </a>
          </footer>
        </section>

      </main>
    </div>
  );
}

// Sub-component for Cards
function Card({ icon, title, subTitle, desc, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.6 }}
      whileHover={{ y: -5 }}
      className="group relative bg-[#0A0A0A] border border-white/5 hover:border-[#00FF41]/50 p-8 rounded-xl transition-colors duration-300"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#00FF41]/5 to-transparent opacity-0 group-hover:opacity-100 rounded-xl transition-opacity duration-500" />
      
      <div className="relative z-10 flex flex-col h-full">
        <div className="text-[#00FF41] mb-6 group-hover:drop-shadow-[0_0_8px_rgba(0,255,65,0.6)] transition-all">
          {icon}
        </div>
        
        <h3 className="text-xl font-bold text-white mb-1 group-hover:text-[#00FF41] transition-colors">
          {title}
        </h3>
        <p className="text-xs font-mono text-gray-500 mb-4 tracking-wide">
          {subTitle}
        </p>
        
        <p className="text-gray-400 text-sm leading-relaxed mt-auto border-t border-white/5 pt-4">
          {desc}
        </p>
      </div>
    </motion.div>
  );
}
